import { ambianceFileUrl } from '@/lib/api';
import { logger } from '@/lib/logger';
import type { ExperienceBlockDraft } from '@matter_extractor/shared';
import { Play } from 'lucide-react'; // pms-design-exception: person-side screen painted by the experience theme, stroke icons kept as drawn
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { READER_TEST_IDS, type ReaderMode } from '../contracts';

export interface SoundBlockProps {
  readonly block: Extract<ExperienceBlockDraft, { type: 'sound' }>;
  readonly mode: ReaderMode;
  /** True once the person has touched the page - the gesture a browser demands. */
  readonly gestured: boolean;
}

/**
 * The Son block (§9.2): « part au premier toucher sur la page (un navigateur
 * mobile refuse tout son sans geste), un petit bouton de lecture reste visible
 * tant que le geste n'a pas eu lieu ».
 *
 * ## Why the gesture, and why a button anyway
 *
 * Every browser refuses `play()` on a page nobody has touched, and a mobile one
 * refuses it hardest. So the reader watches for the FIRST gesture on the page -
 * any gesture, including the click that answers a question - and starts the
 * sound then. The play control is the fallback when that never happens, or when
 * the browser refuses anyway: a page whose sound silently did not start is a
 * page the person cannot repair.
 *
 * ## It plays in `interactive` and nowhere else
 *
 * The Timeline's scene shows a page rather than plays it, and a preview that
 * started a loop while somebody edits the page next to it would be a sound they
 * have to find the source of. In `edit` the control is under the selection
 * target of the frame, so the click selects the block - which is what « la
 * sélection par l'aperçu » means; the inspector carries its own listen button
 * (§9.2).
 *
 * ## A failure is a degradation, never an error state
 *
 * `play()` rejects on a policy the page cannot see. The catch keeps the control
 * on screen - which IS the handling - and warns for the operator. The person
 * must never be shown a broken screen over an ambience.
 */
export function SoundBlock({ block, mode, gestured }: SoundBlockProps): ReactNode {
  const element = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);

  /* `volume` has no React prop: it is a property of the element, not an
   * attribute, so it is written here and re-written when the block changes. */
  useEffect(() => {
    const audio = element.current;
    if (audio === null) return;
    audio.volume = block.volume;
  }, [block.volume]);

  const start = useCallback(() => {
    const audio = element.current;
    if (audio === null) return;
    audio.volume = block.volume;
    try {
      const started: unknown = audio.play();
      if (started instanceof Promise) {
        started.then(
          () => setPlaying(true),
          (cause: unknown) => logger.warn(`reader: sound ${block.id} was refused`, cause),
        );
        return;
      }
      setPlaying(true);
    } catch (cause) {
      logger.warn(`reader: sound ${block.id} could not start`, cause);
    }
  }, [block.id, block.volume]);

  useEffect(() => {
    if (mode !== 'interactive' || !gestured || playing) return;
    start();
  }, [mode, gestured, playing, start]);

  if (block.assetId === null) {
    if (mode !== 'edit') return null;
    return (
      <p className="lecteur-bloc-vide" data-testid={READER_TEST_IDS.emptyAsset}>
        Aucun son choisi
      </p>
    );
  }

  return (
    <>
      {/* biome-ignore lint/a11y/useMediaCaption: an ambience has no speech to caption - §9.2 gives the block a file, a loop and a volume, and nothing else. */}
      <audio ref={element} src={ambianceFileUrl(block.assetId)} loop={block.loop} preload="auto" />
      {playing ? null : (
        <button
          type="button"
          className="lecteur-son"
          data-testid={READER_TEST_IDS.soundPlay}
          aria-label="Jouer le son"
          onClick={start}
        >
          <Play size={16} aria-hidden="true" />
        </button>
      )}
    </>
  );
}
